import React, { useState, useRef } from 'react';
import { Menu } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import ChatGreeting from '../../components/chat/ChatGreeting';
import ChatInput from '../../components/chat/ChatInput';
import SuggestedPrompts from '../../components/chat/SuggestedPrompts';
import ConnectedSourcesBadge from '../../components/chat/ConnectedSourcesBadge';
import PendingThreadCards from '../../components/chat/PendingThreadCards';
import { useChatLayout } from '../../components/layout/ChatLayout';
import { useAuth } from '../../contexts/AuthContext';
import { v2ChatApi } from '../../services/v2-chat-api';
import type { ChatInputHandle } from '../../components/chat/ChatInput';

const IS_DEV = import.meta.env.DEV;

const AGENTS = [
  { id: '', label: 'Default (Chat Assistant)' },
  { id: 'chat', label: 'Chat Assistant' },
  { id: 'anomaly-investigator', label: 'Anomaly Investigator' },
  { id: 'block-builder', label: 'Block Builder' },
  { id: 'dashboard-chat', label: 'Dashboard Builder' },
  { id: 'dashboard-validator', label: 'Dashboard Validator' },
  { id: 'response-verifier', label: 'Response Verifier' },
  { id: 'wiki', label: 'Wiki Agent' },
] as const;

const ChatHome: React.FC = () => {
  const [message, setMessage] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [cacheDiagnostics, setCacheDiagnostics] = useState(false);
  const inputRef = useRef<ChatInputHandle>(null);
  const { isMobile, toggleSidebar } = useChatLayout();
  const { user } = useAuth();
  const showOverrides = IS_DEV || !!user?.isInternalTester;

  // Fetch the canonical model list from the backend registry. Only when the
  // override UI is actually visible — saves a request for everyone else.
  const { data: models } = useQuery({
    queryKey: ['v2-chat-models'],
    queryFn: v2ChatApi.listModels,
    enabled: showOverrides,
    staleTime: Infinity,
  });

  const handleSuggestionSelect = (prompt: string): void => {
    inputRef.current?.setMessage(prompt);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-4 relative">
      {/* Mobile hamburger */}
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          aria-label="Open sidebar"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      <ChatGreeting />

      {showOverrides && (
        <div className="w-full max-w-[680px] mb-3 space-y-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="px-1.5 py-0.5 rounded bg-warn/15 text-warn font-mono font-semibold text-[10px] uppercase tracking-wide">
              Internal
            </span>
            Agent Override
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              className="ml-auto text-xs rounded border border-input bg-card text-foreground px-2 py-1 focus:outline-none focus:ring-1 focus:ring-ring"
            >
              {AGENTS.map((a) => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="px-1.5 py-0.5 rounded bg-warn/15 text-warn font-mono font-semibold text-[10px] uppercase tracking-wide">
              Internal
            </span>
            Model Override
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={!models}
              className="ml-auto text-xs rounded border border-input bg-card text-foreground px-2 py-1 focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
            >
              <option value="">Default (per agent)</option>
              {models?.map((m) => (
                <option key={m.id} value={m.id}>{m.displayName}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="px-1.5 py-0.5 rounded bg-warn/15 text-warn font-mono font-semibold text-[10px] uppercase tracking-wide">
              Internal
            </span>
            Cache Diagnostics
            <select
              value={cacheDiagnostics ? 'on' : ''}
              onChange={(e) => setCacheDiagnostics(e.target.value === 'on')}
              className="ml-auto text-xs rounded border border-input bg-card text-foreground px-2 py-1 focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">Off</option>
              <option value="on">On (Anthropic only)</option>
            </select>
          </label>
        </div>
      )}

      <ChatInput
        ref={inputRef}
        value={message}
        onChange={setMessage}
        agentId={selectedAgent || undefined}
        modelOverride={selectedModel || undefined}
        enableCacheDiagnostics={cacheDiagnostics || undefined}
      />

      <div className="mt-6 w-full flex flex-col items-center">
        <PendingThreadCards />
      </div>

      <div className="mt-2 mb-8">
        <SuggestedPrompts onSelect={handleSuggestionSelect} />
      </div>

      {/* Connected sources strip */}
      <div className="w-full max-w-[700px] mb-4">
        <ConnectedSourcesBadge />
      </div>

      {/* Disclaimer */}
      <p className="absolute bottom-6 text-xs text-muted-foreground text-center">
        Zamino AI may make mistakes. Verify important business decisions.
      </p>
    </div>
  );
};

export default ChatHome;
